import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle2, XCircle, Archive, Pencil, History, Ban, Save, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { StatusBadge, PriorityBadge } from './TaskBadges';
import CollaboratorPicker from './CollaboratorPicker';
import TaskHistory from './TaskHistory';
import { API, SERVICES, PRIORITIES, STATIONS, UNIT_OPTIONS, stationLabel, serviceLabel } from '../../utils/kitchenTasks';
import { formatDateFR } from '../../utils/dateFormat';
import { formatApiError } from '../../services/api';

const fieldCls = 'h-11 rounded-xl bg-gray-100 border-gray-200 text-gray-900';

const Info = ({ label, value, testId }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">{label}</p>
    <p className="text-sm text-gray-900" data-testid={testId}>{value || '—'}</p>
  </div>
);

const TaskDetailDialog = ({ task, open, onOpenChange, collaborators, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [chefComment, setChefComment] = useState('');
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!task) return;
    setEditing(false); setRejectMode(false); setReason(''); setChefComment(''); setShowHistory(false); setBusy(false);
    setForm({
      title: task.title, description: task.description || '', category_name: task.category_name || '', task_date: task.task_date,
      service: task.service, due_time: task.due_time || '', planned_quantity: task.planned_quantity ?? '', unit: task.unit || '',
      priority: task.priority, assigned_user_ids: task.assigned_user_ids || [],
    });
  }, [task]);

  if (!task) return null;

  const run = async (fn, successMsg) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fn();
      toast.success(successMsg);
      onChanged?.(res.data.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const saveEdit = () => run(() => axios.patch(`${API}/kitchen-tasks/${task.id}`, {
    ...form, due_time: form.due_time || '', planned_quantity: form.planned_quantity === '' ? null : parseFloat(form.planned_quantity),
  }), 'Tâche modifiée').then(() => setEditing(false));
  const validate = () => run(() => axios.post(`${API}/kitchen-tasks/${task.id}/validate`, { chef_comment: chefComment }), 'Travail validé');
  const reject = () => {
    if (!reason.trim()) { toast.error('Le motif est obligatoire'); return; }
    run(() => axios.post(`${API}/kitchen-tasks/${task.id}/reject`, { rejection_reason: reason }), 'Tâche renvoyée à corriger').then(() => setRejectMode(false));
  };
  const cancel = () => { if (window.confirm('Annuler cette tâche ?')) run(() => axios.post(`${API}/kitchen-tasks/${task.id}/cancel`), 'Tâche annulée'); };
  const archive = () => { if (window.confirm('Archiver cette tâche ? Elle restera consultable dans les archives.')) run(() => axios.post(`${API}/kitchen-tasks/${task.id}/archive`), 'Tâche archivée'); };
  const publish = () => run(() => axios.patch(`${API}/kitchen-tasks/${task.id}`, { status: 'a_faire' }), 'Tâche publiée');

  const canEdit = !task.archived && !['validee', 'terminee', 'non_realisee', 'annulee'].includes(task.status);
  const canValidate = task.status === 'a_valider';
  const canCancel = ['brouillon', 'a_faire', 'en_cours', 'refusee'].includes(task.status) && !task.archived;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="task-detail-dialog">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2 pr-6">
            <StatusBadge status={task.status} isLate={task.is_late} />
            <PriorityBadge priority={task.priority} />
            {task.archived && <span className="text-xs bg-gray-100 border border-gray-200 rounded-full px-2 py-1 text-gray-600">Archivée</span>}
          </div>
          <DialogTitle className="font-heading text-2xl text-gray-900 mt-2" data-testid="task-detail-title">{task.title}</DialogTitle>
          <DialogDescription className="text-gray-500">{stationLabel(task.category_name)} · {formatDateFR(task.task_date)} · {serviceLabel(task.service)}{task.due_time ? ` · avant ${task.due_time}` : ''}</DialogDescription>
        </DialogHeader>

        {task.status === 'refusee' && task.rejection_reason && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-3 text-sm text-red-800" data-testid="task-detail-rejection"><strong>Motif du refus :</strong> {task.rejection_reason}</div>
        )}

        {task.status === 'non_realisee' && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-3 text-sm text-red-800" data-testid="task-detail-not-done"><strong>Non réalisée{task.not_done_by_name ? ` (${task.not_done_by_name})` : ''} :</strong> {task.not_done_reason}</div>
        )}

        {!editing ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-2">
            <Info label="Quantité demandée" value={task.planned_quantity != null ? `${task.planned_quantity} ${task.unit}` : ''} testId="task-detail-planned" />
            <Info label="Quantité produite" value={task.actual_quantity != null ? `${task.actual_quantity} ${task.actual_unit || task.unit || ''}` : ''} testId="task-detail-actual" />
            <Info label="Collaborateur(s)" value={(task.assigned_user_names || []).join(', ') || 'Non affectée'} testId="task-detail-assignees" />
            <Info label="Créée par" value={task.created_by_name} />
            <Info label="Validée par" value={task.validated_by_name} />
            <Info label="Consigne" value={task.description} testId="task-detail-description" />
            {task.collaborator_comment && <Info label="Commentaire collaborateur" value={task.collaborator_comment} testId="task-detail-collab-comment" />}
            {task.chef_comment && <Info label="Commentaire chef" value={task.chef_comment} />}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2" data-testid="task-edit-form">
            <div className="sm:col-span-2"><Label>Nom</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`mt-1 ${fieldCls}`} data-testid="edit-title-input" /></div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category_name || '__none'} onValueChange={(v) => setForm({ ...form, category_name: v === '__none' ? '' : v })}>
                <SelectTrigger className={`mt-1 ${fieldCls}`} data-testid="edit-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="__none">Sans catégorie</SelectItem>{STATIONS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><input type="date" value={form.task_date} onChange={(e) => setForm({ ...form, task_date: e.target.value })} className={`mt-1 block w-full px-3 text-sm border ${fieldCls}`} data-testid="edit-date-input" /></div>
            <div>
              <Label>Service</Label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger className={`mt-1 ${fieldCls}`} data-testid="edit-service-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{SERVICES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Heure limite</Label><input type="time" value={form.due_time} onChange={(e) => setForm({ ...form, due_time: e.target.value })} className={`mt-1 block w-full px-3 text-sm border ${fieldCls}`} data-testid="edit-due-time-input" /></div>
            <div><Label>Quantité</Label><Input type="number" step="any" value={form.planned_quantity} onChange={(e) => setForm({ ...form, planned_quantity: e.target.value })} className={`mt-1 ${fieldCls}`} data-testid="edit-quantity-input" /></div>
            <div>
              <Label>Unité</Label>
              <Select value={form.unit || '__none'} onValueChange={(v) => setForm({ ...form, unit: v === '__none' ? '' : v })}>
                <SelectTrigger className={`mt-1 ${fieldCls}`} data-testid="edit-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="__none">—</SelectItem>{UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priorité</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className={`mt-1 ${fieldCls}`} data-testid="edit-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label className="mb-2 block">Collaborateur(s)</Label><CollaboratorPicker compact collaborators={collaborators} value={form.assigned_user_ids} onChange={(v) => setForm({ ...form, assigned_user_ids: v })} testIdPrefix="edit-collab" /></div>
            <div className="sm:col-span-2"><Label>Consigne</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-xl bg-gray-100 border-gray-200" data-testid="edit-description-input" /></div>
            <div className="sm:col-span-2 flex gap-2">
              <Button variant="outline" onClick={() => setEditing(false)} className="flex-1 h-11 rounded-full" data-testid="edit-cancel-btn"><X size={16} className="mr-1" />Annuler</Button>
              <Button onClick={saveEdit} disabled={busy} className="flex-1 h-11 rounded-full bg-neon-pink text-gray-900 font-semibold" data-testid="edit-save-btn">{busy ? <Loader2 size={16} className="animate-spin mr-1" /> : <Save size={16} className="mr-1" />}Enregistrer</Button>
            </div>
          </div>
        )}

        {canValidate && !editing && (
          <div className="border-t border-gray-100 pt-4 space-y-3" data-testid="task-validation-zone">
            {!rejectMode ? (
              <>
                <Label>Commentaire (facultatif)</Label>
                <Input value={chefComment} onChange={(e) => setChefComment(e.target.value)} placeholder="Bravo, parfait…" className={fieldCls} data-testid="validate-comment-input" />
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={validate} disabled={busy} className="h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="validate-btn"><CheckCircle2 size={18} className="mr-1.5" />Clore (ancienne tâche)</Button>
                  <Button onClick={() => setRejectMode(true)} disabled={busy} variant="outline" className="h-12 rounded-full border-red-300 text-red-700 hover:bg-red-50 font-semibold" data-testid="reject-mode-btn"><XCircle size={18} className="mr-1.5" />À corriger</Button>
                </div>
              </>
            ) : (
              <>
                <Label>Motif du refus *</Label>
                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex : quantité insuffisante, assaisonnement à revoir…" className="rounded-xl bg-gray-100 border-gray-200" data-testid="reject-reason-input" autoFocus />
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => setRejectMode(false)} className="h-12 rounded-full" data-testid="reject-cancel-btn">Retour</Button>
                  <Button onClick={reject} disabled={busy || !reason.trim()} className="h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="reject-confirm-btn">{busy ? <Loader2 size={16} className="animate-spin mr-1" /> : <XCircle size={16} className="mr-1" />}Confirmer le refus</Button>
                </div>
              </>
            )}
          </div>
        )}

        {!editing && (
          <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
            {task.status === 'brouillon' && <Button onClick={publish} disabled={busy} className="h-11 rounded-full bg-blue-600 text-white" data-testid="publish-btn">Publier (À faire)</Button>}
            {task.status === 'non_realisee' && !task.archived && <Button onClick={publish} disabled={busy} className="h-11 rounded-full bg-blue-600 text-white" data-testid="redo-btn">Remettre à faire</Button>}
            {canEdit && <Button variant="outline" onClick={() => setEditing(true)} className="h-11 rounded-full border-gray-300" data-testid="edit-btn"><Pencil size={16} className="mr-1.5" />Modifier</Button>}
            {canCancel && <Button variant="outline" onClick={cancel} disabled={busy} className="h-11 rounded-full border-gray-300 text-gray-700" data-testid="cancel-task-btn"><Ban size={16} className="mr-1.5" />Annuler la tâche</Button>}
            {!task.archived && <Button variant="outline" onClick={archive} disabled={busy} className="h-11 rounded-full border-gray-300 text-gray-700" data-testid="archive-btn"><Archive size={16} className="mr-1.5" />Archiver</Button>}
            <Button variant="ghost" onClick={() => setShowHistory(!showHistory)} className="h-11 rounded-full text-gray-600 ml-auto" data-testid="history-toggle-btn"><History size={16} className="mr-1.5" />Historique ({(task.history || []).length})</Button>
          </div>
        )}
        {showHistory && <TaskHistory history={task.history} />}
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailDialog;
